import { useRef, useState } from "react";
import { apiError, fileUrl, uploadFile } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, Music, Video, Image as ImageIcon } from "lucide-react";

// Web equivalent of the mobile MediaUploadField — paste a URL or upload a file
// to Emergent object storage. `kind` = "audio" | "video" | "photo".
export function MediaField({ kind, value, onChange, testID }) {
  const t = useT();
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const accept = kind === "audio" ? "audio/*" : kind === "video" ? "video/*" : "image/*";
  const Icon = kind === "audio" ? Music : kind === "video" ? Video : ImageIcon;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { path } = await uploadFile(file, kind);
      onChange(fileUrl(path));
      toast.success(t("cm.media_uploaded"));
    } catch (err) { toast.error(apiError(err, t("cm.media_upload_failed"))); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-2" data-testid={testID}>
      {kind === "photo" && value && (
        <img src={value} alt="" className="h-28 w-full rounded-lg border border-border object-cover" />
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Icon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={t("cm.media_url_ph")} data-testid={testID ? `${testID}-url` : undefined} />
        </div>
        {value && (
          <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-full" onClick={() => onChange("")} data-testid={testID ? `${testID}-clear` : undefined}>
            <X className="h-4 w-4" />
          </Button>
        )}
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} />
        <Button type="button" variant="outline" className="shrink-0 gap-1.5 rounded-full" disabled={uploading} onClick={() => ref.current?.click()} data-testid={testID ? `${testID}-upload` : undefined}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          {uploading ? t("cm.media_uploading") : t("cm.media_upload")}
        </Button>
      </div>
    </div>
  );
}
